import { useSettingEuHomepageUpdate } from "@faire/deprecated-retailer-visitor-shared/settings/useSettingEuHomepageUpdate";
import { useQuery } from "@faire/web-api--source/endpoints/www/api/v3/layout/logged-out/homepage-categories/get-hooks";
import { ILoggedOutHomepageCategoriesResponse } from "@faire/web-api--source/faire/layout/ILoggedOutHomepageCategoriesResponse";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import { useViewport } from "@faire/web/common/hooks/useViewport";
import { Flex } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { Carousel } from "@faire/web/ui-labs/Carousel/Carousel";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React from "react";

import { ExploreCategoryCard } from "./ExploreCategoryCard";

// Hardcoded category data for when EU Homepage Update setting is false
const HARDCODED_CATEGORIES: ILoggedOutHomepageCategoriesResponse.ICategoryItem[] =
  [
    {
      category_name: "Home decor",
      category_image: IImage.build({
        url: "https://cdn.faire.com/fastly/030d7a7e019d50c97dd5cf4d6730c577f9b92e40ce37ded3e646cc92e9abbb7b.jpeg",
        alt_text: "Home decor",
      }),
      category_link: "/category/Home%20Decor",
    },
    {
      category_name: "Food & drink",
      category_image: IImage.build({
        url: "https://cdn.faire.com/fastly/58651e22cc9baca6b49d1009a91186f60fbc0818666fb0442ef37388e48dbb71.jpeg",
        alt_text: "Food & drink",
      }),
      category_link: "/category/Food%20&%20Drink",
    },
    {
      category_name: "Women",
      category_image: IImage.build({
        url: "https://cdn.faire.com/fastly/64b0a7fe30e85f3307614ebd6594e11966c14f93bfdfbbf7c4fede3409cfe334.jpeg",
        alt_text: "Women",
      }),
      category_link: "/category/Women",
    },
    {
      category_name: "Beauty & wellness",
      category_image: IImage.build({
        url: "https://cdn.faire.com/fastly/2419edbf7c63fa408c84a4b9773087d48b250acd86180408cac5e7c6a6807797.jpeg",
        alt_text: "Beauty & wellness",
      }),
      category_link: "/category/Beauty%20&%20Wellness",
    },
    {
      category_name: "Jewelry",
      category_image: IImage.build({
        url: "https://cdn.faire.com/fastly/11e102417a9b0167ed6eb8be7adf313a1a8d551e6fbb8957c6dd5ed8e73bf527.jpeg",
        alt_text: "Jewelry",
      }),
      category_link: "/category/Jewelry",
    },
    {
      category_name: "Paper & novelty",
      category_image: IImage.build({
        url: "https://cdn.faire.com/fastly/8516fa639191487dc85242b32747f71e10dfb7c3653f1291068dd1b6242e3408.jpeg",
        alt_text: "Paper & novelty",
      }),
      category_link: "/category/Paper%20&%20Novelty",
    },
    {
      category_name: "Kids & baby",
      category_image: IImage.build({
        url: "https://cdn.faire.com/fastly/e8e77ed440682f44b621c4d7388a603455a3b1b2c5d89b32e0cfce47f88e288e.jpeg",
        alt_text: "Kids & baby",
      }),
      category_link: "/category/Kids%20&%20Baby",
    },
    {
      category_name: "Pets",
      category_image: IImage.build({
        url: "https://cdn.faire.com/fastly/6dfe35854ed01af7ec82358b46c65be7228fa5c840ccee60a2a7ab24c1bdb4f9.jpeg",
        alt_text: "Pets",
      }),
      category_link: "/category/Pets",
    },
    {
      category_name: "Men",
      category_image: IImage.build({
        url: "https://cdn.faire.com/fastly/38744b45f74f6689f68f87eb9bc022ae9f293d830017592cfc248888c3308fd8.jpeg",
        alt_text: "Men",
      }),
      category_link: "/category/Men",
    },
  ];

export const ExploreCategoriesCarousel: React.FC<{
  onViewEvent?: () => void;
}> = ({ onViewEvent }) => {
  const ref = useDoActionWhenVisible(onViewEvent);
  const { isMobile } = useViewport();
  const useEuHomepageUpdate = useSettingEuHomepageUpdate();

  const { data, isLoading, isError } = useQuery({
    staleTime: Infinity,
    enabled: useEuHomepageUpdate,
  });

  // Use hardcoded data when EU Homepage Update setting is false
  const category_items = useEuHomepageUpdate
    ? data?.category_items
    : HARDCODED_CATEGORIES;

  // Show loading/error states only when using EP and there's an issue
  if (useEuHomepageUpdate && (isLoading || isError)) {
    return null;
  }

  // Always check if we have categories to display
  if (!category_items?.length) {
    return null;
  }

  return (
    <div
      ref={ref}
      className="relative overflow-hidden p-4 pt-8 md:p-8 lg:p-12 2xl:px-20 2xl:py-12"
    >
      <Typography
        variant={{
          mobileAndAbove: "pageHeaderSerifRegular",
          xLargeAndAbove: "displaySSerifRegular",
          xxLargeDesktop: "displayMSerifRegular",
        }}
      >
        <StrictLocalMsg
          id="C1LWS3"
          defaultMessage="Explore categories"
          description={{
            text: "Explore categories carousel title on logged out homepage",
          }}
        />
      </Typography>
      <Spacer height="3x" />

      {isMobile ? (
        <Flex gap="2x" direction="column">
          {category_items.map(
            (
              category: ILoggedOutHomepageCategoriesResponse.ICategoryItem,
              index: number
            ) => (
              <ExploreCategoryCard
                key={category.category_name ?? `category-${index}`}
                category={category}
                aspectRatio={3.75}
              />
            )
          )}
        </Flex>
      ) : (
        <Carousel
          items={category_items}
          renderItem={(
            category: ILoggedOutHomepageCategoriesResponse.ICategoryItem,
            index: number
          ) => (
            <ExploreCategoryCard
              key={category.category_name ?? `category-${index}`}
              category={category}
              aspectRatio={1.4}
            />
          )}
          itemsPerSlide={{
            mobileAndAbove: 2,
            tabletAndAbove: 3,
          }}
          showNavigationArrows={{
            mobileAndAbove: false,
            tabletAndAbove: true,
          }}
          itemGap={16}
        />
      )}
    </div>
  );
};
